// iter61 — Personal Audio: PA paging + Music streaming + per-user prefs.
// Mounts in Layout's top bar. Single popover with Music / Page / Preferences tabs.
// Listens to window 'app:ws_event' for pa:tts and pa:voice_* events.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Headphones, Radio, Music2, Megaphone, Settings, Search, Heart, HeartOff,
  Volume2, VolumeX, Play, Pause, Square, Mic, MicOff, X, BellOff, Bell, ChevronDown,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const ROLE_OPTIONS = ['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing', 'Operator'];

const ROLE_LABEL = {
  Admin: 'Admin',
  InventoryManager: 'Inventory Manager',
  ProductionSupervisor: 'Supervisor',
  Purchasing: 'Purchasing',
  Operator: 'Operator',
};

function api() {
  const token = localStorage.getItem('token');
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

// Web Audio chime (no asset)
// iter78j — share a single AudioContext across chime + speech-unlock so iOS
// Safari can resume it on the first user gesture.
let _sharedAudioCtx = null;
function getAudioCtx() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!_sharedAudioCtx) _sharedAudioCtx = new Ctx();
    return _sharedAudioCtx;
  } catch (_) { return null; }
}

function playPageChime() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      // iOS — must resume from a user gesture. If we're not in a gesture,
      // resume() returns a pending promise; the chime simply won't sound.
      ctx.resume().catch(() => {});
    }
    const tones = [880, 660, 880];
    let t = ctx.currentTime;
    tones.forEach((freq) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.5, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      osc.connect(g).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.25);
      t += 0.25;
    });
  } catch (_) { /* noop */ }
}

// iter78k — expose unlock state so the UI can show an indicator
let _audioUnlocked = false;
const _audioUnlockSubs = new Set();
function isAudioUnlocked() { return _audioUnlocked; }
function onAudioUnlock(cb) { _audioUnlockSubs.add(cb); return () => _audioUnlockSubs.delete(cb); }
function unlockAudioOnce() {
  if (_audioUnlocked) return;
  _audioUnlocked = true;
  // 1. Resume the shared AudioContext
  try {
    const ctx = getAudioCtx();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  } catch (_) { /* noop */ }
  // 2. Speak an empty utterance so the synthesis engine is "primed"
  try {
    const synth = window.speechSynthesis;
    if (synth) {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0; // silent
      u.rate = 1;
      synth.speak(u);
    }
  } catch (_) { /* noop */ }
  _audioUnlockSubs.forEach((cb) => { try { cb(true); } catch (_) { /* noop */ } });
}

if (typeof window !== 'undefined' && !window.__paAudioUnlockBound) {
  window.__paAudioUnlockBound = true;
  const onFirstGesture = () => {
    unlockAudioOnce();
    window.removeEventListener('pointerdown', onFirstGesture, true);
    window.removeEventListener('touchstart', onFirstGesture, true);
    window.removeEventListener('keydown', onFirstGesture, true);
  };
  window.addEventListener('pointerdown', onFirstGesture, true);
  window.addEventListener('touchstart', onFirstGesture, true);
  window.addEventListener('keydown', onFirstGesture, true);
}

function speak(text, onEnd) {
  try {
    const synth = window.speechSynthesis;
    if (!synth) { if (onEnd) onEnd(); return; }
    // iter78j — re-prime synthesis in case the engine paused itself (iOS quirk)
    if (synth.paused) { try { synth.resume(); } catch (_) { /* noop */ } }
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = 1.0;
    u.onend = () => { if (onEnd) onEnd(); };
    u.onerror = () => { if (onEnd) onEnd(); };
    synth.speak(u);
  } catch (_) { if (onEnd) onEnd(); }
}

// ---------- Music player (radio + custom URL) ----------
const useMusic = (prefs) => {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // {kind, name, url, image, artist}
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(prefs?.music_volume ?? 60);
  const [duckedFromVolume, setDuckedFromVolume] = useState(null); // remembers volume before duck

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume / 100;
  }, [volume]);

  const play = useCallback((track) => {
    if (!track || !track.url) return;
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = 'anonymous';
    }
    audioRef.current.src = track.url;
    audioRef.current.volume = volume / 100;
    audioRef.current.play().then(() => {
      setCurrent(track);
      setPlaying(true);
    }).catch((e) => {
      toast.error(`Cannot play stream: ${e.message || 'mixed-content or unreachable'}`);
    });
  }, [volume]);

  const pause = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && current) {
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [current]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setCurrent(null);
    setPlaying(false);
  }, []);

  const duck = useCallback(() => {
    if (!playing) return;
    setDuckedFromVolume(volume);
    pause();
  }, [playing, volume, pause]);

  const restoreFromDuck = useCallback(() => {
    if (duckedFromVolume !== null) {
      setVolume(duckedFromVolume);
      setDuckedFromVolume(null);
      resume();
    }
  }, [duckedFromVolume, resume]);

  return { current, playing, volume, setVolume, play, pause, resume, stop, duck, restoreFromDuck };
};

// ---------- Component ----------
export default function PersonalAudio({ currentUser }) {
  const [prefs, setPrefs] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('music');
  const [searchQ, setSearchQ] = useState('');
  const [searchKind, setSearchKind] = useState('radio');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const [customName, setCustomName] = useState('');
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [incomingPage, setIncomingPage] = useState(null); // {from_name, text, mode}
  // iter64 — radio filters: country + genre
  const [filterCountry, setFilterCountry] = useState(localStorage.getItem('audio:radio_country') || '');
  const [filterTag, setFilterTag] = useState(localStorage.getItem('audio:radio_tag') || '');
  const [countries, setCountries] = useState([]);
  const [tags, setTags] = useState([]);

  const music = useMusic(prefs);
  // iter78k — track audio-unlock state for the visible indicator
  const [audioUnlocked, setAudioUnlocked] = useState(isAudioUnlocked());
  useEffect(() => {
    const off = onAudioUnlock((v) => setAudioUnlocked(v));
    return off;
  }, []);

  // Load prefs + global config
  useEffect(() => {
    (async () => {
      try {
        const [p, g] = await Promise.all([
          api().get('/audio/me/prefs'),
          api().get('/admin/audio-config').catch(() => ({ data: null })),
        ]);
        setPrefs(p.data);
        if (g.data) setGlobalConfig(g.data);
      } catch (e) {
        console.warn('Failed to load audio prefs', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (prefs && prefs.music_volume !== undefined) {
      music.setVolume(prefs.music_volume);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs?.music_volume]);

  // Save prefs
  const savePrefs = useCallback(async (patch) => {
    try {
      const res = await api().put('/audio/me/prefs', patch);
      setPrefs(res.data);
    } catch (e) {
      toast.error('Failed to save preferences');
    }
  }, []);

  // ---------- Listen for PA events ----------
  useEffect(() => {
    const handler = (ev) => {
      const msg = ev.detail;
      if (!msg || !msg.type) return;
      if (msg.type === 'pa:tts') {
        const d = msg.data || {};
        // Auto-duck music
        if (d.auto_duck) music.duck();
        if (d.page_chime) playPageChime();
        // iter78m — keep from_user_id + page_id so the banner can reply
        setIncomingPage({ id: d.page_id, from_name: d.from_name, from_user_id: d.from_user_id, text: d.text, mode: 'tts', priority: d.priority });
        // Speak after chime
        setTimeout(() => {
          speak(`Page from ${d.from_name || 'Supervisor'}. ${d.text || ''}`, () => {
            // iter78m — keep the banner up longer (12s) so the recipient has time
            // to tap Reply. Replying explicitly extends this further via state.
            setTimeout(() => {
              setIncomingPage((cur) => (cur && cur._replying ? cur : null));
              if (d.auto_duck) music.restoreFromDuck();
            }, 12000);
          });
        }, d.page_chime ? 700 : 0);
      } else if (msg.type === 'pa:voice_invite') {
        // iter65 — VoiceCallManager handles the full WebRTC flow.
        // Here we only handle the side-effects: auto-duck music + chime, since
        // the call overlay itself already plays a ring tone.
        const d = msg.data || {};
        if (d.auto_duck) music.duck();
        // Don't add page banner — VoiceCallManager shows its own Accept/Decline UI.
      }
    };
    window.addEventListener('app:ws_event', handler);
    return () => window.removeEventListener('app:ws_event', handler);
  }, [music]);

  // ---------- Music search ----------
  const runSearch = useCallback(async () => {
    setSearching(true);
    try {
      if (searchKind === 'radio') {
        const params = new URLSearchParams();
        if (searchQ.trim()) params.set('q', searchQ.trim());
        if (filterCountry) params.set('country', filterCountry);
        if (filterTag) params.set('tag', filterTag);
        params.set('limit', '40');
        // If no query AND no filters → show top stations; otherwise search
        const hasAny = !!(searchQ.trim() || filterCountry || filterTag);
        const r = hasAny
          ? await api().get(`/audio/radio/search?${params.toString()}`)
          : await api().get(`/audio/radio/top?limit=40`);
        setResults(r.data || []);
      } else if (searchKind === 'jamendo') {
        const r = await api().get(`/audio/jamendo/search?q=${encodeURIComponent(searchQ)}&limit=30`);
        const data = r.data || {};
        if (data.note) toast.info(data.note);
        setResults((data.results || []).map(t => ({
          id: t.id, name: t.name, url: t.url, image: t.image, artist: t.artist, kind: 'jamendo',
        })));
      }
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchQ, searchKind, filterCountry, filterTag]);

  // Load country + tag lists once when popover first opens (cached client-side after)
  useEffect(() => {
    if (!open || searchKind !== 'radio') return;
    if (countries.length > 0 && tags.length > 0) return;
    (async () => {
      try {
        const [c, t] = await Promise.all([
          api().get('/audio/radio/countries'),
          api().get('/audio/radio/tags?limit=80'),
        ]);
        setCountries(c.data || []);
        setTags(t.data || []);
      } catch (_) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchKind]);

  // Auto-rerun search when filters change (debounced lightly)
  useEffect(() => {
    if (!open || tab !== 'music' || searchKind !== 'radio') return;
    const t = setTimeout(() => runSearch(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCountry, filterTag]);

  useEffect(() => {
    if (open && tab === 'music' && results.length === 0) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const playRadio = (st) => music.play({ kind: 'radio', id: st.id, name: st.name, url: st.url, image: st.favicon });
  const playJamendo = (t) => music.play({ kind: 'jamendo', id: t.id, name: t.name, url: t.url, image: t.image, artist: t.artist });
  const playCustom = () => {
    if (!customUrl) return;
    music.play({ kind: 'custom', id: customUrl, name: customName || 'Custom Stream', url: customUrl });
  };

  const isFavorite = (item) => (prefs?.favorites || []).some(f => f.id === item.id && f.kind === (item.kind || searchKind));
  const toggleFavorite = async (item) => {
    if (!prefs) return;
    const kind = item.kind || searchKind;
    const exists = (prefs.favorites || []).some(f => f.id === item.id && f.kind === kind);
    let next;
    if (exists) {
      next = (prefs.favorites || []).filter(f => !(f.id === item.id && f.kind === kind));
    } else {
      next = [...(prefs.favorites || []), {
        kind, id: item.id, name: item.name, url: item.url, image: item.image || item.favicon, artist: item.artist,
      }].slice(0, 50);
    }
    await savePrefs({ favorites: next });
  };

  // Toggle role permission
  const toggleRole = (role) => {
    if (!prefs) return;
    const has = (prefs.allow_roles || []).includes(role);
    const next = has ? (prefs.allow_roles || []).filter(r => r !== role) : [...(prefs.allow_roles || []), role];
    savePrefs({ allow_roles: next });
  };

  if (!prefs) return null;
  const muted = !music.playing;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            data-testid="personal-audio-trigger"
            title={audioUnlocked ? 'Audio · Page · Music' : 'Audio · TAP TO ENABLE on iOS Safari'}
            onClick={() => unlockAudioOnce()}
          >
            <Headphones className={`h-5 w-5 ${music.playing ? 'text-emerald-600' : 'text-slate-600'}`} />
            {music.playing && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            )}
            {prefs.dnd && (
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500" />
            )}
            {/* iter78k — speaker indicator: green when audio unlocked, red strike when locked */}
            <span
              className={`absolute -top-1 -left-1 inline-flex items-center justify-center h-3.5 w-3.5 rounded-full ${
                audioUnlocked ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
              }`}
              data-testid={`audio-unlock-status-${audioUnlocked ? 'on' : 'off'}`}
              title={audioUnlocked ? 'Audio enabled' : 'Audio locked — tap to enable'}
              aria-hidden
            >
              {audioUnlocked ? <Volume2 className="h-2.5 w-2.5" /> : <VolumeX className="h-2.5 w-2.5" />}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="end" data-testid="personal-audio-popover">
          <div className="border-b px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Headphones className="h-4 w-4 text-slate-600" />
              <div>
                <div className="text-sm font-semibold">Personal Audio</div>
                {music.current && (
                  <div className="text-xs text-slate-500 truncate max-w-[260px]" data-testid="personal-audio-now-playing">
                    {music.playing ? 'Playing' : 'Paused'} · {music.current.name}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {music.current && (
                muted
                  ? <Button variant="ghost" size="icon" onClick={music.resume} title="Play" data-testid="audio-mini-play"><Play className="h-4 w-4" /></Button>
                  : <Button variant="ghost" size="icon" onClick={music.pause} title="Pause" data-testid="audio-mini-pause"><Pause className="h-4 w-4" /></Button>
              )}
              {music.current && (
                <Button variant="ghost" size="icon" onClick={music.stop} title="Stop" data-testid="audio-mini-stop">
                  <Square className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-3 mx-3 my-2" data-testid="personal-audio-tabs">
              <TabsTrigger value="music" data-testid="audio-tab-music"><Music2 className="h-3.5 w-3.5 mr-1" />Music</TabsTrigger>
              <TabsTrigger value="page" data-testid="audio-tab-page"><Megaphone className="h-3.5 w-3.5 mr-1" />Page</TabsTrigger>
              <TabsTrigger value="prefs" data-testid="audio-tab-prefs"><Settings className="h-3.5 w-3.5 mr-1" />Prefs</TabsTrigger>
            </TabsList>

            {/* MUSIC TAB */}
            <TabsContent value="music" className="px-4 pb-4 max-h-[460px] overflow-y-auto space-y-3">
              <div className="flex items-center gap-2">
                <Volume2 className="h-4 w-4 text-slate-500" />
                <Slider
                  className="flex-1"
                  value={[music.volume]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={(v) => music.setVolume(v[0])}
                  onValueCommit={(v) => savePrefs({ music_volume: v[0] })}
                  data-testid="audio-volume-slider"
                />
                <span className="text-xs text-slate-500 w-8 text-right">{music.volume}%</span>
              </div>

              <div className="flex items-center gap-2">
                <Select value={searchKind} onValueChange={setSearchKind}>
                  <SelectTrigger className="w-32" data-testid="audio-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="radio">Radio</SelectItem>
                    <SelectItem value="jamendo">Jamendo</SelectItem>
                    <SelectItem value="custom">Custom URL</SelectItem>
                  </SelectContent>
                </Select>
                {searchKind !== 'custom' && (
                  <>
                    <Input
                      placeholder={searchKind === 'radio' ? 'Search stations' : 'Search Jamendo tracks'}
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                      data-testid="audio-search-input"
                    />
                    <Button size="icon" variant="outline" onClick={runSearch} disabled={searching} data-testid="audio-search-btn">
                      <Search className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>

              {/* iter64 — Country + Genre filters for Radio */}
              {searchKind === 'radio' && (
                <div className="flex items-center gap-2">
                  <Select
                    value={filterCountry || 'any'}
                    onValueChange={(v) => {
                      const next = v === 'any' ? '' : v;
                      setFilterCountry(next);
                      if (next) localStorage.setItem('audio:radio_country', next); else localStorage.removeItem('audio:radio_country');
                    }}
                  >
                    <SelectTrigger className="flex-1" data-testid="audio-country-filter">
                      <SelectValue placeholder="Any country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="any">Any country</SelectItem>
                      {countries.slice(0, 80).map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name} ({c.stationcount.toLocaleString()})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={filterTag || 'any'}
                    onValueChange={(v) => {
                      const next = v === 'any' ? '' : v;
                      setFilterTag(next);
                      if (next) localStorage.setItem('audio:radio_tag', next); else localStorage.removeItem('audio:radio_tag');
                    }}
                  >
                    <SelectTrigger className="flex-1" data-testid="audio-tag-filter">
                      <SelectValue placeholder="Any genre" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="any">Any genre</SelectItem>
                      {tags.map(t => (
                        <SelectItem key={t.name} value={t.name}>
                          {t.name} ({t.stationcount.toLocaleString()})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(filterCountry || filterTag) && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setFilterCountry('');
                        setFilterTag('');
                        localStorage.removeItem('audio:radio_country');
                        localStorage.removeItem('audio:radio_tag');
                      }}
                      title="Clear filters"
                      data-testid="audio-clear-filters"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}

              {searchKind === 'custom' && (
                <div className="space-y-2">
                  <Input
                    placeholder="Stream URL (mp3 / icecast / shoutcast)"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    data-testid="audio-custom-url"
                  />
                  <Input
                    placeholder="Display name (optional)"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    data-testid="audio-custom-name"
                  />
                  <Button onClick={playCustom} className="w-full" data-testid="audio-play-custom">
                    <Play className="h-4 w-4 mr-2" />Play Custom Stream
                  </Button>
                </div>
              )}

              {/* Favorites */}
              {(prefs.favorites || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Favorites</div>
                  <div className="space-y-1">
                    {prefs.favorites.map((f, idx) => (
                      <div key={`${f.kind}-${f.id}-${idx}`} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50" data-testid={`audio-fav-${idx}`}>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => music.play({ ...f })}
                          data-testid={`audio-fav-play-${idx}`}>
                          <Play className="h-3.5 w-3.5" />
                        </Button>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{f.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">{f.kind}{f.artist ? ` · ${f.artist}` : ''}</div>
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => toggleFavorite(f)} data-testid={`audio-fav-remove-${idx}`}>
                          <HeartOff className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search results */}
              {searchKind !== 'custom' && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    {searching ? 'Searching…' : (
                      searchQ || filterCountry || filterTag
                        ? `Results${filterCountry ? ` · ${filterCountry}` : ''}${filterTag ? ` · ${filterTag}` : ''}`
                        : 'Top Stations'
                    )}
                  </div>
                  <div className="space-y-1">
                    {results.map((r, idx) => (
                      <div key={`${r.id || idx}`} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50" data-testid={`audio-result-${idx}`}>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => searchKind === 'radio' ? playRadio(r) : playJamendo(r)}
                          data-testid={`audio-result-play-${idx}`}>
                          <Play className="h-3.5 w-3.5" />
                        </Button>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{r.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {searchKind === 'radio'
                              ? `${r.country || ''} ${r.tags ? '· ' + r.tags.split(',').slice(0, 3).join(', ') : ''}`
                              : (r.artist || '')}
                          </div>
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => toggleFavorite({ ...r, kind: searchKind })} data-testid={`audio-result-fav-${idx}`}>
                          <Heart className={`h-3.5 w-3.5 ${isFavorite({ ...r, kind: searchKind }) ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} />
                        </Button>
                      </div>
                    ))}
                    {!searching && results.length === 0 && (
                      <div className="text-xs text-slate-500 py-3 text-center">No results.</div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* PAGE TAB */}
            <TabsContent value="page" className="px-4 pb-4 space-y-3">
              <div className="text-xs text-slate-500">
                Page on-shift staff. They'll hear a chime + your message via TTS, with optional live-voice follow-up.
              </div>
              <Button
                className="w-full"
                onClick={() => setPageDialogOpen(true)}
                disabled={globalConfig && !globalConfig.pa_globally_enabled}
                data-testid="audio-open-page-dialog"
              >
                <Megaphone className="h-4 w-4 mr-2" /> New Page
              </Button>
              {globalConfig && !globalConfig.pa_globally_enabled && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  PA system is currently disabled by your administrator.
                </div>
              )}
            </TabsContent>

            {/* PREFS TAB */}
            <TabsContent value="prefs" className="px-4 pb-4 max-h-[460px] overflow-y-auto space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Allow Pages</Label>
                    <div className="text-xs text-slate-500">Master toggle</div>
                  </div>
                  <Switch checked={!!prefs.allow_pages} onCheckedChange={(v) => savePrefs({ allow_pages: v })} data-testid="prefs-allow-pages" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Do Not Disturb</Label>
                    <div className="text-xs text-slate-500">Block all but emergency Admin pages</div>
                  </div>
                  <Switch checked={!!prefs.dnd} onCheckedChange={(v) => savePrefs({ dnd: v })} data-testid="prefs-dnd" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Auto-Pause on Page</Label>
                    <div className="text-xs text-slate-500">Pause music when paged, resume after</div>
                  </div>
                  <Switch checked={!!prefs.auto_duck} onCheckedChange={(v) => savePrefs({ auto_duck: v })} data-testid="prefs-auto-duck" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Chime Before Page</Label>
                    <div className="text-xs text-slate-500">Play short chime before TTS</div>
                  </div>
                  <Switch checked={!!prefs.page_chime} onCheckedChange={(v) => savePrefs({ page_chime: v })} data-testid="prefs-page-chime" />
                </div>
                {/* iter78j — explicit audio unlock for iOS Safari users */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div>
                    <Label className="text-sm font-medium">Test Audio</Label>
                    <div className="text-xs text-slate-500">Tap once on iOS/Safari to enable chime &amp; spoken pages</div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      unlockAudioOnce();
                      playPageChime();
                      setTimeout(() => speak('Audio enabled. You will hear pages from now on.'), 500);
                    }}
                    data-testid="prefs-test-audio"
                  >
                    Test
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Roles allowed to page me</Label>
                <div className="text-xs text-slate-500 mb-2">Select broad categories. Per-user overrides win.</div>
                <div className="flex flex-wrap gap-1.5">
                  {ROLE_OPTIONS.map(role => {
                    const on = (prefs.allow_roles || []).includes(role);
                    return (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleRole(role)}
                        className={`text-xs px-2 py-1 rounded-full border ${on ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                        data-testid={`prefs-role-${role}`}
                      >
                        {ROLE_LABEL[role]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>

      {/* Page composer dialog */}
      <PageDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        currentUser={currentUser}
        globalConfig={globalConfig}
      />

      {/* Incoming-page banner overlay */}
      {incomingPage && (
        <div className="fixed top-2 right-2 z-50 max-w-sm bg-blue-600 text-white shadow-2xl rounded-lg p-4 border-2 border-blue-300 animate-in slide-in-from-right" data-testid="incoming-page-banner">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide opacity-80">
                {incomingPage.priority === 'emergency' ? '⚠ EMERGENCY PAGE' : (incomingPage.priority === 'urgent' ? 'Urgent Page' : 'Page')}
              </div>
              <div className="font-semibold text-base">{incomingPage.from_name}</div>
              {incomingPage.text && <div className="text-sm mt-1 leading-snug">{incomingPage.text}</div>}
              {incomingPage.mode === 'voice' && <div className="text-xs mt-2 opacity-80">Live voice page incoming</div>}
              {incomingPage.mode !== 'voice' && (
                <button
                  type="button"
                  onClick={() => {
                    unlockAudioOnce();
                    playPageChime();
                    setTimeout(() => speak(`Page from ${incomingPage.from_name || 'Supervisor'}. ${incomingPage.text || ''}`), 400);
                  }}
                  className="mt-2 text-xs underline hover:no-underline mr-3"
                  data-testid="incoming-page-replay"
                >
                  Tap to play / replay audio
                </button>
              )}
              {/* iter78m — quick-reply UI right on the banner */}
              {incomingPage.from_user_id && !incomingPage._replying && (
                <button
                  type="button"
                  onClick={() => setIncomingPage({ ...incomingPage, _replying: true, _replyText: '' })}
                  className="mt-2 inline-flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded-md"
                  data-testid="incoming-page-reply-open"
                >
                  ↪ Reply
                </button>
              )}
              {incomingPage._replying && (
                <div className="mt-2 space-y-1" data-testid="incoming-page-reply-form">
                  <input
                    autoFocus
                    type="text"
                    value={incomingPage._replyText || ''}
                    onChange={(e) => setIncomingPage({ ...incomingPage, _replyText: e.target.value })}
                    placeholder={`Reply to ${incomingPage.from_name || 'sender'}…`}
                    className="w-full rounded-md px-2 py-1 text-sm text-slate-900"
                    data-testid="incoming-page-reply-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setIncomingPage({ ...incomingPage, _replying: false });
                    }}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-white text-blue-700 hover:bg-blue-50 h-7"
                      disabled={!incomingPage._replyText || !incomingPage._replyText.trim()}
                      onClick={async () => {
                        try {
                          await api().post('/audio/page', {
                            recipients: [incomingPage.from_user_id],
                            mode: 'tts',
                            text: incomingPage._replyText.trim(),
                            priority: 'normal',
                            reply_to: incomingPage.id || null, // iter78m — bypass allow_roles on the original sender
                          });
                          toast.success(`Reply sent to ${incomingPage.from_name || 'sender'}`);
                          setIncomingPage(null);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Reply failed');
                        }
                      }}
                      data-testid="incoming-page-reply-send"
                    >
                      Send reply
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-white hover:bg-white/20 h-7"
                      onClick={() => setIncomingPage({ ...incomingPage, _replying: false })}
                      data-testid="incoming-page-reply-cancel"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => setIncomingPage(null)} className="text-white/80 hover:text-white" data-testid="incoming-page-dismiss" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ============== Page Composer Dialog ==============
function PageDialog({ open, onClose, currentUser, globalConfig }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [text, setText] = useState('');
  const [mode, setMode] = useState('tts');
  const [priority, setPriority] = useState('normal');
  const [broadcastRole, setBroadcastRole] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api().get('/audio/pageable-users');
        setUsers(r.data || []);
      } catch (e) {
        toast.error('Failed to load users');
      }
    })();
    setSelected([]);
    setText('');
    setBroadcastRole('');
  }, [open]);

  const toggleUser = (id) => {
    if (mode === 'voice') {
      // iter65 — voice calls are 1:1
      setSelected([id]);
      return;
    }
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // iter65 — when switching to voice, trim selection to first user
  useEffect(() => {
    if (mode === 'voice' && selected.length > 1) {
      setSelected(prev => prev.slice(0, 1));
    }
  }, [mode, selected.length]);

  const send = async () => {
    if (mode === 'tts' && !text.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (mode === 'voice') {
      if (broadcastRole) { toast.error('Voice calls cannot be broadcast — pick one user'); return; }
      if (selected.length !== 1) { toast.error('Voice calls are 1-to-1 — pick exactly one user'); return; }
    }
    if (selected.length === 0 && !broadcastRole) {
      toast.error('Pick recipient(s) or a broadcast role');
      return;
    }
    setSending(true);
    try {
      const res = await api().post('/audio/page', {
        recipients: selected,
        mode,
        text: text.trim() || null,
        priority,
        broadcast_role: broadcastRole || null,
      });
      if (mode === 'voice') {
        if ((res.data?.delivered || 0) === 0) {
          toast.error('Recipient unreachable');
        } else {
          // Look up the chosen user's name from the existing list, then kick off the call.
          const target = users.find(u => u.id === selected[0]);
          window.dispatchEvent(new CustomEvent('app:start_voice_call', {
            detail: { userId: selected[0], userName: target?.name || 'Recipient', priority },
          }));
        }
      } else {
        toast.success(`Paged ${res.data.delivered} user(s)${res.data.blocked ? ` — ${res.data.blocked} blocked` : ''}`);
      }
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Page failed';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const canBroadcast = ['Admin', 'ProductionSupervisor'].includes(currentUser?.role);
  const canEmergency = currentUser?.role === 'Admin';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="page-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="h-4 w-4" /> Send Page</DialogTitle>
          <DialogDescription>Send a TTS message or live-voice page to staff. Recipients can opt-out via their preferences.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="page-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tts">TTS (text-to-speech)</SelectItem>
                  <SelectItem value="voice" disabled={!globalConfig?.voice_pages_enabled}>Voice (push-to-talk)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="page-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="emergency" disabled={!canEmergency}>Emergency (bypass DND)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Broadcast to role (optional)</Label>
              <Select value={broadcastRole || 'none'} onValueChange={(v) => setBroadcastRole(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="page-broadcast-role" disabled={!canBroadcast}><SelectValue placeholder="Pick recipients individually" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Pick individually —</SelectItem>
                  {ROLE_OPTIONS.map(r => <SelectItem key={r} value={r}>All {ROLE_LABEL[r]}s</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {mode === 'tts' && (
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. John, please come to the shipping desk."
                rows={3}
                maxLength={400}
                data-testid="page-text-input"
              />
              <div className="text-[10px] text-slate-500 text-right">{text.length}/400</div>
            </div>
          )}
          {mode === 'voice' && (
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-800">
              Voice mode is 1-to-1: pick exactly one online user. They'll get a ring + Accept/Decline. Once accepted, both mics open for {Math.floor((globalConfig?.max_voice_seconds || 60) / 60)}m {(globalConfig?.max_voice_seconds || 60) % 60}s.
            </div>
          )}

          {!broadcastRole && (
            <div>
              <Label className="text-xs mb-2 block">Recipients ({selected.length} selected)</Label>
              <div className="border rounded max-h-56 overflow-y-auto divide-y">
                {users.length === 0 && <div className="text-xs text-slate-500 p-3 text-center">No other users.</div>}
                {users.map(u => (
                  <label key={u.id} className={`flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer ${!u.can_page ? 'opacity-50' : ''}`} data-testid={`page-user-row-${u.id}`}>
                    <input
                      type="checkbox"
                      checked={selected.includes(u.id)}
                      disabled={!u.can_page || (priority !== 'emergency' && u.dnd)}
                      onChange={() => toggleUser(u.id)}
                      data-testid={`page-user-cb-${u.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{u.name}</div>
                      <div className="text-[10px] text-slate-500">{ROLE_LABEL[u.role] || u.role}</div>
                    </div>
                    {u.online ? <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700 text-[10px]">online</Badge>
                              : <Badge variant="outline" className="text-slate-400 text-[10px]">offline</Badge>}
                    {u.dnd && <Badge variant="outline" className="bg-red-50 border-red-300 text-red-700 text-[10px]"><BellOff className="h-2.5 w-2.5 mr-0.5" />DND</Badge>}
                    {!u.can_page && <Badge variant="outline" className="bg-slate-50 text-slate-500 text-[10px]">no permission</Badge>}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="page-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="page-send-btn">
            <Megaphone className="h-4 w-4 mr-2" />
            {sending ? 'Sending…' : 'Send Page'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
